import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FroalaEditorComponent from "react-froala-wysiwyg";
import Autocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "font-awesome/css/font-awesome.css";
import { fetchPrograms, fetchPolicyLabels, addPolicy } from "../../api/faqApi";
import CommonSnackbar from "../CommonSnackbar";

// Utility to strip HTML tags from a string
function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

export interface ProgramOption {
  label: string;
  value: string;
  code?: string;
}

interface CreatePolicyDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues?: {
    program?: string;
    course?: string;
    description?: string;
  };
  onPolicyCreated?: (programCode: string) => void;
}

const CreatePolicyDialog: React.FC<CreatePolicyDialogProps> = ({
  open,
  onClose,
  initialValues = {},
  onPolicyCreated,
}) => {
  const [formData, setFormData] = useState({
    program: initialValues.program || "",
    course: initialValues.course || "",
    description: initialValues.description || "",
  });
  const [errors, setErrors] = useState({
    program: false,
    course: false,
    description: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);
  const [programOptions, setProgramOptions] = useState<ProgramOption[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [labelOptions, setLabelOptions] = useState<
    { label: string; value: string; label_id: string }[]
  >([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    setFormData({
      program: initialValues.program || "",
      course: initialValues.course || "",
      description: initialValues.description || "",
    });
  }, [initialValues, open]);

  useEffect(() => {
    if (open) {
      setLoadingPrograms(true);
      fetchPrograms("")
        .then((programs) => setProgramOptions(programs))
        .catch(() => setProgramOptions([]))
        .finally(() => setLoadingPrograms(false));
      setLoadingLabels(true);
      fetchPolicyLabels()
        .then((labels) =>
          setLabelOptions(
            labels.map((l) => ({
              label: l.label,
              value: l.value,
              label_id: l.label_id || l.value || l.label, // fallback for safety
            }))
          )
        )
        .catch(() => setLabelOptions([]))
        .finally(() => setLoadingLabels(false));
    }
  }, [open]);

  const handleDescriptionChange = (content: string) => {
    setFormData((prev) => ({ ...prev, description: content }));
    if (errors.description) {
      setErrors((prev) => ({ ...prev, description: false }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      program: !formData.program.trim(),
      course: !formData.course.trim(),
      description: !formData.description.trim(),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      await addPolicy({
        program_code: formData.program,
        label_id: formData.course,
        description: stripHtml(formData.description),
      });
      setFormData({ program: "", course: "", description: "" });
      setErrors({ program: false, course: false, description: false });
      setSnackbar({
        open: true,
        message: "Policy added successfully!",
        severity: "success",
      });
      if (onPolicyCreated) {
        onPolicyCreated(formData.program);
      }
      onClose();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add policy. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndAdd = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSavingAndAdding(true);
    try {
      await addPolicy({
        program_code: formData.program,
        label_id: formData.course,
        description: stripHtml(formData.description),
      });
      setFormData((prev) => ({
        ...prev,
        course: "",
        description: "",
      }));
      setErrors((prev) => ({
        ...prev,
        course: false,
        description: false,
      }));
      setSnackbar({
        open: true,
        message: "Policy added successfully!",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add policy. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSavingAndAdding(false);
    }
  };

  const handleClose = () => {
    setFormData({ program: "", course: "", description: "" });
    setErrors({ program: false, course: false, description: false });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "60vw",
          height: "85vh",
          maxWidth: "60vw",
          maxHeight: "85vh",
          p: 0,
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#f7f7f7",
          fontWeight: 700,
          fontSize: 20,
          pb: 2,
          pt: 3,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #eee",
          borderRadius: "6px 6px 0 0",
        }}
      >
        Create Policy
        <IconButton onClick={handleClose} sx={{ ml: 2 }}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, overflowY: "auto", flexGrow: 1 }}>
        <Box sx={{ my: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Program <span style={{ color: "red" }}>*</span>
          </Typography>
          <Autocomplete
            options={programOptions}
            getOptionLabel={(option) => `${option.label} (${option.code})`}
            loading={loadingPrograms}
            value={
              programOptions.find((p) => p.code === formData.program) || null
            }
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                program:
                  newValue && newValue.code
                    ? newValue.code
                    : newValue
                    ? newValue.value
                    : "",
              }));
              if (errors.program) {
                setErrors((prev) => ({ ...prev, program: false }));
              }
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2">{option.label}</Typography>
                  {option.code && (
                    <Typography
                      variant="caption"
                      sx={{ ml: 1, color: "text.secondary", fontSize: "14px" }}
                    >
                      ({option.code})
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextField
                {...params}
                label="Program"
                placeholder="Select a program"
                variant="outlined"
                sx={{ background: "#fff" }}
                error={errors.program}
                helperText={errors.program ? "Program is required" : ""}
              />
            )}
            ListboxProps={{
              sx: {
                paddingBottom: "10px",
                maxHeight: 215, // Set the dropdown height
                overflowY: "auto", // Ensure the content is scrollable
              },
            }}
          />
        </Box>
        <Box sx={{ my: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Label <span style={{ color: "red" }}>*</span>
          </Typography>
          <Autocomplete
            options={labelOptions}
            getOptionLabel={(option) => option.label}
            loading={loadingLabels}
            value={
              labelOptions.find((l) => l.label_id === formData.course) || null
            }
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                course: newValue ? newValue.label_id : "",
              }));
              if (errors.course) {
                setErrors((prev) => ({ ...prev, course: false }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Label"
                placeholder="Select a label"
                variant="outlined"
                sx={{ background: "#fff" }}
                error={errors.course}
                helperText={errors.course ? "Label is required" : ""}
              />
            )}
          />
        </Box>
        <Box sx={{ my: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Description <span style={{ color: "red" }}>*</span>
          </Typography>
          <FroalaEditorComponent
            tag="textarea"
            model={formData.description || ""}
            onModelChange={handleDescriptionChange}
            config={{
              placeholderText: "Enter description",
              charCounterCount: false,
              heightMin: 120,
              heightMax: 230,
              toolbarSticky: false,
            }}
          />
          {errors.description && (
            <Typography color="error" variant="caption">
              Description is required
            </Typography>
          )}
        </Box>
      </DialogContent>
      <Box sx={{ borderTop: "1px solid #eee" }}>
        <DialogActions sx={{ p: 2, display: "flex", gap: 2 }}>
          <Button
            onClick={handleSaveAndAdd}
            sx={{
              color: "#000",
              border: "1px solid #000",
              width: 106,
              height: 38,
            }}
            disabled={isSaving || isSavingAndAdding}
          >
            {isSavingAndAdding ? (
              <CircularProgress size={24} sx={{ color: "#000" }} />
            ) : (
              "Save & Add"
            )}
          </Button>
          <Button
            onClick={handleSave}
            sx={{ color: "#fff", background: "#000", width: 106, height: 38 }}
            disabled={isSaving || isSavingAndAdding}
          >
            {isSaving ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Box>
      <CommonSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Dialog>
  );
};

export default CreatePolicyDialog;
